/**
 * Le dessin d'un attelage — le grand schéma et la vignette.
 *
 * Repris tel quel de la maquette validée le 03/09/2026. **Un dessin de côté en
 * SVG, pas une vraie 3D** : décidé par l'utilisateur ce jour-là. Une image de
 * synthèse coûterait un fichier par combinaison, ne dirait pas quelle remorque
 * est attelée, et ne se laisserait pas cliquer pièce par pièce.
 *
 * **IL MONTRE L'ÉTAT, ET NON UN PANIER À REMPLIR.** C'est la correction
 * demandée par l'utilisateur devant la première maquette : « je peux uniquement
 * attacher une combinaison, il n'y a aucune notion des affectations en cours ».
 * Chaque pièce attelée porte donc sa date de début et une croix pour la
 * détacher ; chaque place vide est en pointillé et se remplit en choisissant
 * dans la colonne des libres.
 *
 * LES COULEURS SONT CELLES DE LA FEUILLE DE STYLE, prises par leur nom.
 *
 * Une première version les recopiait en clair, sur l'idée qu'un attribut `fill`
 * de SVG n'accepterait pas `var(--accent)`. **C'était faux, et vérifié** : un
 * attribut de présentation SVG se lit comme une propriété CSS, `var()` compris
 * — constaté dans Chromium le 03/09/2026, `getComputedStyle(...).fill` rendant
 * bien `rgb(15, 76, 129)`.
 *
 * Ce qui suit n'est donc plus une copie à tenir en accord avec `styles.css`,
 * mais un renvoi. Le jour où l'accent de Stafa changera, le camion changera
 * avec lui, sans que personne ait à y penser.
 */

export const BLEU = 'var(--accent)';
export const GRIS = 'var(--trait-fort)';
export const FOND = 'var(--accent-doux)';
export const REFUS = 'var(--refus)';

/** Une roue : deux cercles concentriques. */
function Roue({
  x,
  y,
  r,
  couleur,
  pointille,
  mince = false,
}: {
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly couleur: string;
  readonly pointille: string | undefined;
  readonly mince?: boolean;
}) {
  const e = mince ? 2 : 3;
  return (
    <>
      <circle
        cx={x}
        cy={y}
        r={r}
        fill="none"
        stroke={couleur}
        strokeWidth={e}
        strokeDasharray={pointille}
      />
      <circle
        cx={x}
        cy={y}
        r={Math.round(r * 0.38)}
        fill="none"
        stroke={couleur}
        strokeWidth={Math.max(1, e - 1)}
        strokeDasharray={pointille}
      />
    </>
  );
}

/**
 * La croix de détachement, posée SUR la pièce qu'elle retire.
 *
 * Un vrai `<button>` en superposition plutôt qu'un `<g role="button">` : c'est
 * ce qui donne le clavier, le focus et l'annonce sans avoir à les réécrire. Le
 * SVG dessine, le bouton agit.
 */
function Croix({
  x,
  y,
  cible,
  quandDetacher,
}: {
  readonly x: number;
  readonly y: number;
  readonly cible: string;
  readonly quandDetacher: () => void;
}) {
  return (
    <g
      className="detacher"
      onClick={quandDetacher}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          quandDetacher();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Détacher ${cible}`}
    >
      <circle
        cx={x}
        cy={y}
        r={14}
        fill="var(--surface)"
        stroke={REFUS}
        strokeWidth={2}
      />
      <line
        x1={x - 5}
        y1={y - 5}
        x2={x + 5}
        y2={y + 5}
        stroke={REFUS}
        strokeWidth={2.5}
      />
      <line
        x1={x + 5}
        y1={y - 5}
        x2={x - 5}
        y2={y + 5}
        stroke={REFUS}
        strokeWidth={2.5}
      />
    </g>
  );
}

function Depuis({
  texte,
  x,
  y,
}: {
  readonly texte: string | null;
  readonly x: number;
  readonly y: number;
}) {
  if (texte === null) return null;
  return (
    <text x={x} y={y} textAnchor="middle" className="depuis-schema" fill={GRIS}>
      depuis le {texte}
    </text>
  );
}

export interface ProprietesSchema {
  readonly vehicule: string | null;
  readonly remorque: string | null;
  readonly chauffeur: string | null;
  /** Dates de début, déjà mises en forme « JJ/MM/AAAA ». */
  readonly depuisVehicule?: string | null;
  readonly depuisRemorque?: string | null;
  readonly depuisChauffeur?: string | null;
  /** Absent : la croix n'est pas dessinée, même si la pièce est là. */
  readonly quandDetacherRemorque?: () => void;
  readonly quandDetacherChauffeur?: () => void;
}

/**
 * Le grand schéma. Chaque pièce porte son nom, ou le mot qui dit qu'elle
 * manque.
 */
export function SchemaAttelage({
  vehicule,
  remorque,
  chauffeur,
  depuisVehicule = null,
  depuisRemorque = null,
  depuisChauffeur = null,
  quandDetacherRemorque,
  quandDetacherChauffeur,
}: ProprietesSchema) {
  const rOn = remorque !== null;
  const vOn = vehicule !== null;
  const cOn = chauffeur !== null;

  const rCol = rOn ? BLEU : GRIS;
  const rFond = rOn ? FOND : 'none';
  const rP = rOn ? undefined : '6 5';
  const vCol = vOn ? BLEU : GRIS;
  const vFond = vOn ? FOND : 'none';
  const vP = vOn ? undefined : '6 5';
  const cCol = cOn ? BLEU : GRIS;
  const cFond = cOn ? BLEU : 'none';
  const cP = cOn ? undefined : '4 4';

  const nomR = remorque ?? 'choisir une remorque';
  const nomV = vehicule ?? 'choisir un véhicule';
  const nomC = chauffeur ?? 'choisir un chauffeur';

  return (
    <svg
      viewBox="70 -16 830 308"
      className="schema-attelage"
      role="img"
      aria-label={`Attelage de ${nomV}`}
    >
      <line x1={85} y1={222} x2={885} y2={222} stroke="var(--trait)" strokeWidth={2} />

      {/* la remorque */}
      <g>
        <rect
          x={90}
          y={62}
          width={470}
          height={118}
          rx={4}
          fill={rFond}
          stroke={rCol}
          strokeWidth={3}
          strokeDasharray={rP}
        />
        <line
          x1={90}
          y1={96}
          x2={560}
          y2={96}
          stroke={rCol}
          strokeWidth={1.5}
          strokeDasharray={rP}
        />
        <rect
          x={120}
          y={180}
          width={440}
          height={12}
          fill={rFond}
          stroke={rCol}
          strokeWidth={3}
          strokeDasharray={rP}
        />
        <Roue x={180} y={196} r={26} couleur={rCol} pointille={rP} />
        <Roue x={240} y={196} r={26} couleur={rCol} pointille={rP} />
        <Roue x={300} y={196} r={26} couleur={rCol} pointille={rP} />
        <line
          x1={420}
          y1={192}
          x2={420}
          y2={218}
          stroke={rCol}
          strokeWidth={3}
          strokeDasharray={rP}
        />
        <line
          x1={405}
          y1={218}
          x2={435}
          y2={218}
          stroke={rCol}
          strokeWidth={3}
          strokeDasharray={rP}
        />
      </g>

      {/* le véhicule moteur */}
      <g>
        <rect
          x={560}
          y={176}
          width={300}
          height={16}
          rx={3}
          fill={vFond}
          stroke={vCol}
          strokeWidth={3}
          strokeDasharray={vP}
        />
        <path
          d="M 660 176 L 660 84 Q 660 70 674 70 L 798 70 Q 814 70 822 86 L 848 140 Q 856 154 856 168 L 856 176 Z"
          fill={vFond}
          stroke={vCol}
          strokeWidth={3}
          strokeLinejoin="round"
          strokeDasharray={vP}
        />
        <path
          d="M 798 86 L 814 90 L 838 140 L 798 140 Z"
          fill="none"
          stroke={vCol}
          strokeWidth={2}
          strokeDasharray={vP}
        />
        <path
          d="M 668 70 L 668 52 Q 668 46 676 46 L 786 46 Q 796 46 796 56 L 796 70"
          fill="none"
          stroke={vCol}
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeDasharray={vP}
        />
        <rect
          x={596}
          y={164}
          width={52}
          height={14}
          rx={3}
          fill={vFond}
          stroke={vCol}
          strokeWidth={2.5}
          strokeDasharray={vP}
        />
        <Roue x={636} y={196} r={26} couleur={vCol} pointille={vP} />
        <Roue x={812} y={196} r={26} couleur={vCol} pointille={vP} />
      </g>

      {/* le chauffeur, dans la cabine */}
      <g>
        <circle
          cx={700}
          cy={106}
          r={13}
          fill={cFond}
          stroke={cCol}
          strokeWidth={2.5}
          strokeDasharray={cP}
        />
        <path
          d="M 682 150 Q 682 124 700 124 Q 718 124 718 150 Z"
          fill={cFond}
          stroke={cCol}
          strokeWidth={2.5}
          strokeDasharray={cP}
        />
      </g>

      {rOn && quandDetacherRemorque !== undefined && (
        <Croix x={548} y={74} cible={nomR} quandDetacher={quandDetacherRemorque} />
      )}
      {cOn && quandDetacherChauffeur !== undefined && (
        <Croix x={748} y={100} cible={nomC} quandDetacher={quandDetacherChauffeur} />
      )}

      <text
        x={325}
        y={252}
        textAnchor="middle"
        className="etiquette-schema"
        fill={rCol}
      >
        {nomR}
      </text>
      <Depuis texte={depuisRemorque} x={325} y={272} />
      <text
        x={758}
        y={252}
        textAnchor="middle"
        className="etiquette-schema"
        fill={vCol}
      >
        {nomV}
      </text>
      <Depuis texte={depuisVehicule} x={758} y={272} />
      <text x={700} y={6} textAnchor="middle" className="etiquette-schema" fill={cCol}>
        {nomC}
      </text>
      <Depuis texte={depuisChauffeur} x={700} y={26} />
    </svg>
  );
}

/**
 * La même chose en miniature : une par véhicule du parc, en haut de l'écran.
 *
 * C'est elle qui répond à « qu'est-ce qui est attelé en ce moment » d'un seul
 * coup d'œil, sans avoir à ouvrir chaque fiche. Le véhicule moteur y est
 * toujours plein — c'est lui qu'on choisit ; seuls la remorque et le chauffeur
 * s'y montrent absents.
 */
export function VignetteAttelage({
  vehicule,
  remorque,
  chauffeur,
}: {
  readonly vehicule: string;
  readonly remorque: string | null;
  readonly chauffeur: string | null;
}) {
  const rOn = remorque !== null;
  const cOn = chauffeur !== null;
  const rCol = rOn ? BLEU : GRIS;
  const rP = rOn ? undefined : '3 3';
  const rFond = rOn ? FOND : 'none';
  const cCol = cOn ? BLEU : GRIS;

  return (
    <svg
      viewBox="0 0 230 72"
      className="vignette-attelage"
      role="img"
      aria-label={`${vehicule}${rOn ? ` avec ${remorque as string}` : ' sans remorque'}`}
    >
      <rect
        x={8}
        y={14}
        width={118}
        height={30}
        rx={2}
        fill={rFond}
        stroke={rCol}
        strokeWidth={2}
        strokeDasharray={rP}
      />
      <rect
        x={14}
        y={44}
        width={112}
        height={4}
        fill={rFond}
        stroke={rCol}
        strokeWidth={2}
        strokeDasharray={rP}
      />
      <Roue x={38} y={50} r={7} couleur={rCol} pointille={rP} mince />
      <Roue x={56} y={50} r={7} couleur={rCol} pointille={rP} mince />
      <Roue x={74} y={50} r={7} couleur={rCol} pointille={rP} mince />
      <rect
        x={126}
        y={42}
        width={82}
        height={6}
        rx={2}
        fill={FOND}
        stroke={BLEU}
        strokeWidth={2}
      />
      <path
        d="M 150 42 L 150 20 Q 150 16 154 16 L 188 16 Q 192 16 194 21 L 202 34 Q 205 38 205 42 Z"
        fill={FOND}
        stroke={BLEU}
        strokeWidth={2}
        strokeLinejoin="round"
      />
      <Roue x={140} y={50} r={7} couleur={BLEU} pointille={undefined} mince />
      <Roue x={190} y={50} r={7} couleur={BLEU} pointille={undefined} mince />
      <circle
        cx={163}
        cy={27}
        r={4.5}
        fill={cOn ? BLEU : 'none'}
        stroke={cCol}
        strokeWidth={1.6}
      />
      <line x1={8} y1={58} x2={222} y2={58} stroke="var(--trait)" strokeWidth={1} />
    </svg>
  );
}
